import { createBrowserRouter, type RouteObject } from "react-router";
import { AppShell } from "@/components/layout/AppShell";
import { NotFound, RouteError } from "@/components/layout/Errors";

// One application (decisions 37 and 43): the public map and its pages; the back-office, signed in through Keycloak (decision 46):
// /valider, /editeur, /compte, /admin, and /connexion where Keycloak sends back. Each page is loaded when first visited.

const page =
  <M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M) =>
  async () => ({ Component: (await load())[name] as React.ComponentType });

const routes: RouteObject[] = [
  {
    path: "/",
    element: <AppShell />,
    errorElement: <RouteError />,
    children: [
      { index: true, lazy: page(() => import("@/features/public/MapPage"), "MapPage") },
      { path: "export/:id", lazy: page(() => import("@/features/public/ExportPage"), "ExportPage") },
      { path: "journal", lazy: page(() => import("@/features/public/JournalPage"), "JournalPage") },
      { path: "sources", lazy: page(() => import("@/features/public/SourcesPage"), "SourcesPage") },
      { path: "a-propos", lazy: page(() => import("@/features/public/AboutPage"), "AboutPage") },
      { path: "connexion", lazy: page(() => import("@/features/account/SignInCallback"), "SignInCallback") },
      {
        path: "compte",
        lazy: page(() => import("@/features/account/AccountSpace"), "AccountSpace"),
        children: [{ index: true, lazy: page(() => import("@/features/admin/KeysPage"), "MyKeysPage") }],
      },
      {
        path: "valider",
        lazy: page(() => import("@/features/validate/ValidateSpace"), "ValidateSpace"),
        children: [
          { index: true, lazy: page(() => import("@/features/validate/ValidatePage"), "ValidatePage") },
          { path: "taches/:taskId", lazy: page(() => import("@/features/validate/ValidatePage"), "ValidatePage") },
        ],
      },
      {
        path: "editeur",
        lazy: page(() => import("@/features/editor/EditorSpace"), "EditorSpace"),
        children: [
          { index: true, lazy: page(() => import("@/features/editor/ChangesetsPage"), "ChangesetsPage") },
          { path: "lots/:id", lazy: page(() => import("@/features/editor/ChangesetPage"), "ChangesetPage") },
        ],
      },
      {
        path: "admin",
        lazy: page(() => import("@/features/admin/AdminSpace"), "AdminSpace"),
        children: [
          { index: true, lazy: page(() => import("@/features/admin/OverviewPage"), "OverviewPage") },
          { path: "cles", lazy: page(() => import("@/features/admin/KeysPage"), "KeysPage") },
          { path: "utilisateurs", lazy: page(() => import("@/features/admin/UsersPage"), "UsersPage") },
          { path: "lots", lazy: page(() => import("@/features/admin/ChangesetsPage"), "ChangesetsPage") },
          { path: "lots/:id", lazy: page(() => import("@/features/admin/ChangesetPage"), "ChangesetPage") },
          { path: "versions", lazy: page(() => import("@/features/admin/VersionsPage"), "VersionsPage") },
          { path: "surveillance", lazy: page(() => import("@/features/admin/MonitoringPage"), "MonitoringPage") },
          { path: "historique", lazy: page(() => import("@/features/admin/AuditPage"), "AuditPage") },
        ],
      },
      { path: "*", element: <NotFound /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
